import React, { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import ScoreApplicant from "../../../../api/ScoreApplicant";
import JobPosition from "../../../../api/JobPosition";
import Feeds from "../../../../components/molecules/Feeds/Feeds";
import {
  buildScoreApplicantPayload,
  getPositionFromResponse,
  normalizePastedJson,
  parseApplicationRecord,
  parseResumeFromUrl,
} from "./scoreApplicantUtils";

const formatTime = () =>
  new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

const ScoreApplicantPanel = ({ callback = false }) => {
  const [jsonInput, setJsonInput] = useState("");
  const [parsedRecords, setParsedRecords] = useState([]);
  const [parseError, setParseError] = useState("");
  const [logs, setLogs] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (callback) {
      callback("Score Applicant");
    }
  }, []);

  const addLog = (name, comment, type = "success") => {
    setLogs((prev) => [
      {
        id: `${Date.now()}-${prev.length}`,
        type,
        person: { name, comment },
        date: formatTime(),
        dateTime: new Date().toISOString(),
      },
      ...prev,
    ]);
  };

  const handleParseJson = () => {
    setParseError("");
    setParsedRecords([]);

    if (!jsonInput.trim()) {
      setParseError("Paste application JSON to continue.");
      return;
    }

    try {
      const records = normalizePastedJson(jsonInput).map(parseApplicationRecord);
      setParsedRecords(records);
      toast.success(`Parsed ${records.length} application(s)`);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to parse JSON input";
      setParseError(message);
      toast.error(message);
    }
  };

  const scoreSingleApplication = async (record) => {
    const label = `Application ${record.application_id}`;

    const positionResponse = await JobPosition.getById(record.job_position_id);
    const position = getPositionFromResponse(positionResponse);
    const resumeText = await parseResumeFromUrl(record.resume_url);
    const payload = buildScoreApplicantPayload({
      position,
      record,
      resumeText,
    });

    const scoreResponse = await ScoreApplicant.score(payload);

    const score = scoreResponse.data?.score;
    addLog(
      label,
      score != null
        ? `Scored successfully. Score: ${score}`
        : "Scored successfully.",
      "success"
    );

    return scoreResponse.data;
  };

  const handleScoreAll = async () => {
    if (parsedRecords.length === 0) {
      toast.error("Parse JSON first.");
      return;
    }

    setIsProcessing(true);
    addLog("Batch", `Starting score for ${parsedRecords.length} application(s)`, "warning");

    let successCount = 0;
    let failureCount = 0;

    for (const record of parsedRecords) {
      const label = `Application ${record.application_id}`;
      try {
        await scoreSingleApplication(record);
        successCount += 1;
      } catch (error) {
        failureCount += 1;
        const message =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          error?.message ||
          "Unknown error";
        addLog(label, message, "error");
      }
    }

    addLog(
      "Batch complete",
      `${successCount} succeeded, ${failureCount} failed`,
      failureCount > 0 ? "warning" : "success"
    );

    if (failureCount === 0) {
      toast.success(`Scored ${successCount} application(s)`);
    } else {
      toast.error(`${failureCount} application(s) failed`);
    }

    setIsProcessing(false);
  };

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900">Score Applicant</h2>
        <p className="mt-1 text-sm text-gray-600">
          Paste application JSON to score missing applicants and save results to
          the database. Resume text is parsed via{" "}
          <code className="text-xs">/api/parse-pdf</code> when the server
          supports it, otherwise directly from{" "}
          <code className="text-xs">resume_url</code>.
        </p>

        <textarea
          className="mt-4 min-h-[240px] w-full rounded-md border border-gray-300 p-3 font-mono text-sm text-gray-800 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          placeholder='Paste JSON array, e.g. [{ "id": "10993", "job_seeker_id": "24676", "job_id": "1707", ... }]'
          value={jsonInput}
          onChange={(event) => setJsonInput(event.target.value)}
          disabled={isProcessing}
        />

        {parseError && (
          <p className="mt-2 text-sm text-red-600">{parseError}</p>
        )}

        <div className="mt-4 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={handleParseJson}
            disabled={isProcessing}
            className="rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-800 hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Parse JSON
          </button>
          <button
            type="button"
            onClick={handleScoreAll}
            disabled={isProcessing || parsedRecords.length === 0}
            className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isProcessing ? "Scoring..." : "Score All"}
          </button>
        </div>
      </div>

      {parsedRecords.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-200 px-6 py-4">
            <h3 className="text-base font-semibold text-gray-900">
              Parsed Applications ({parsedRecords.length})
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">
                    Application ID
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">
                    Job Seeker ID
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">
                    Job ID
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">
                    Resume
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {parsedRecords.map((record) => (
                  <tr key={record.application_id}>
                    <td className="px-4 py-3 text-gray-900">
                      {record.application_id}
                    </td>
                    <td className="px-4 py-3 text-gray-900">
                      {record.job_seeker_id}
                    </td>
                    <td className="px-4 py-3 text-gray-900">
                      {record.job_position_id}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {record.file_name}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {logs.length > 0 && (
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <h3 className="mb-4 text-base font-semibold text-gray-900">Activity</h3>
          <Feeds data={logs} />
        </div>
      )}
    </div>
  );
};

export default ScoreApplicantPanel;
